/**
 * Translation Service
 *
 * Manages translation workflow:
 * 1. Capture PDF page as image from iframe
 * 2. Send image to Gemini API for OCR + translation
 * 3. Cache results for future use
 *
 * The backend sends images directly to Gemini's vision API,
 * which performs both OCR and translation in one step.
 */

import {
  PdfParagraph,
  TranslatedParagraph,
  TranslateRequest,
  TranslateResponse,
} from "./translationTypes.js";
import {
  savePageTranslation,
  loadPageTranslation,
  clearPageTranslation,
} from "./translationStorage.js";

/**
 * API base URL - uses relative path for same-origin requests.
 * In production, this routes through Firebase Hosting to the API function.
 * In development, use the test server on port 5002.
 */
const API_BASE_URL = typeof window !== "undefined" && window.location.hostname === "localhost"
  ? "http://localhost:5002"
  : "/api";

/**
 * Capture a PDF page as a base64 image from the PDF.js viewer iframe.
 * Uses postMessage to communicate with the iframe's orbit-integration.js script.
 *
 * @param iframe - Reference to the PDF.js viewer iframe
 * @param pageIndex - 0-indexed page number to capture
 * @returns Base64 encoded image data URL
 */
export async function capturePageAsImage(
  iframe: HTMLIFrameElement,
  pageIndex: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow) {
      reject(new Error("Cannot access iframe content"));
      return;
    }

    // Generate unique request ID
    const requestId = `capture-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Set up message handler for response
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== "pageImageCaptured" || data.requestId !== requestId) {
        return;
      }

      // Clean up listener
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);

      if (data.success && data.imageData) {
        resolve(data.imageData);
      } else {
        reject(new Error(data.error || "Failed to capture page image"));
      }
    };

    // Set timeout for response
    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      reject(new Error("Timeout waiting for page image capture"));
    }, 30000); // 30 second timeout

    window.addEventListener("message", handleMessage);

    // Send capture request to iframe
    iframeWindow.postMessage(
      {
        type: "capturePageImage",
        pageIndex,
        requestId,
      },
      "*"
    );
  });
}

/**
 * Call the translation API endpoint with a page image.
 *
 * @param pageImage - Base64 encoded image data URL
 * @param pageIndex - 0-indexed page number
 * @returns Translation response from Gemini
 */
async function callTranslateAPI(
  pageImage: string,
  pageIndex: number
): Promise<TranslateResponse> {
  const request: TranslateRequest = {
    pageImage,
    pageIndex,
  };

  const response = await fetch(`${API_BASE_URL}/translate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error || `Translation failed: ${response.status}`
    );
  }

  return response.json();
}

/**
 * Convert API response to our internal format.
 * Assigns color indices based on paragraph order.
 */
function convertApiResponse(
  apiResponse: TranslateResponse,
  pageIndex: number,
  startColorIndex: number
): { paragraphs: PdfParagraph[]; translations: TranslatedParagraph[] } {
  const paragraphs: PdfParagraph[] = [];
  const translations: TranslatedParagraph[] = [];

  console.log(`[translation] Converting API response for pageIndex=${pageIndex}, got ${apiResponse.paragraphs.length} paragraphs`);

  apiResponse.paragraphs.forEach((p, index) => {
    // Generate a unique ID that includes the actual pageIndex to avoid duplicates
    // Gemini sometimes returns IDs with wrong page numbers, so we override with correct pageIndex
    const uniqueId = `para-p${pageIndex}-${index}`;

    // Log the paragraph ID to verify it matches the expected page
    console.log(`[translation] Paragraph ${index}: id=${uniqueId} (original: ${p.id}), bbox.y0=${p.bbox.y0.toFixed(3)}, text preview: "${p.translatedText.substring(0, 50)}..."`);

    paragraphs.push({
      id: uniqueId,
      pageIndex,
      text: p.originalText,
      bbox: p.bbox,
    });

    translations.push({
      paragraphId: uniqueId,
      originalText: p.originalText,
      translatedText: p.translatedText,
      colorIndex: startColorIndex + index,
    });
  });

  return { paragraphs, translations };
}

/**
 * Translate a single page.
 * Checks cache first, then calls API if needed.
 *
 * @param docId - Document ID
 * @param iframe - PDF viewer iframe reference
 * @param pageIndex - Page to translate (0-indexed)
 * @param startColorIndex - Starting color index for this page's paragraphs
 * @param forceRefresh - If true, skip cache and re-translate (clears existing cache)
 * @returns Paragraphs and translations for the page
 */
async function translatePage(
  docId: string,
  iframe: HTMLIFrameElement,
  pageIndex: number,
  startColorIndex: number,
  forceRefresh: boolean = false
): Promise<{
  paragraphs: PdfParagraph[];
  translations: TranslatedParagraph[];
  fromCache: boolean;
}> {
  // If force refresh, clear existing cache for this page
  if (forceRefresh) {
    console.log(`[translation] FORCE REFRESH for pageIndex=${pageIndex}, clearing cache...`);
    await clearPageTranslation(docId, pageIndex);
  }

  // Check cache first (unless force refresh)
  const cached = !forceRefresh ? await loadPageTranslation(docId, pageIndex) : null;
  if (cached) {
    console.log(`[translation] CACHE HIT for pageIndex=${pageIndex}, cached.pageIndex=${cached.pageIndex}`);
    console.log(`[translation] Cached paragraphs count: ${cached.paragraphs.length}`);

    // Log each cached paragraph to trace data
    cached.paragraphs.forEach((p, i) => {
      const textPreview = cached.translations[i]?.translatedText?.substring(0, 60) || 'N/A';
      console.log(`[translation] Cached paragraph ${i}: id=${p.id}, pageIndex in para=${p.pageIndex}, bbox.y0=${p.bbox.y0.toFixed(3)}, text: "${textPreview}..."`);
    });

    // Ensure paragraphs have unique IDs and correct pageIndex (defensive fix for cache consistency)
    // Regenerate IDs to match the format used in convertApiResponse
    const paragraphs = cached.paragraphs.map((p, i) => ({
      ...p,
      id: `para-p${pageIndex}-${i}`, // Generate unique ID based on actual pageIndex
      pageIndex, // Override with the requested pageIndex to ensure consistency
    }));
    // Update color indices and paragraph IDs to match
    const translations = cached.translations.map((t, i) => ({
      ...t,
      paragraphId: `para-p${pageIndex}-${i}`, // Match the regenerated paragraph ID
      colorIndex: startColorIndex + i,
    }));
    return {
      paragraphs,
      translations,
      fromCache: true,
    };
  }

  // Capture page as image
  console.log(`[translation] CACHE MISS for pageIndex=${pageIndex}, capturing page image...`);
  const pageImage = await capturePageAsImage(iframe, pageIndex);
  console.log(`[translation] Captured image for pageIndex=${pageIndex}, image size: ${pageImage.length} chars`);

  // Call API
  console.log(`[translation] Calling translate API for pageIndex=${pageIndex}...`);
  const apiResponse = await callTranslateAPI(pageImage, pageIndex);

  // Convert to our format
  const { paragraphs, translations } = convertApiResponse(
    apiResponse,
    pageIndex,
    startColorIndex
  );

  // Cache results
  console.log(`[translation] Saving to cache: docId=${docId}, pageIndex=${pageIndex}, paragraphs count=${paragraphs.length}`);
  paragraphs.forEach((p, i) => {
    const textPreview = translations[i]?.translatedText?.substring(0, 60) || 'N/A';
    console.log(`[translation] Saving paragraph ${i}: id=${p.id}, pageIndex=${p.pageIndex}, text: "${textPreview}..."`);
  });
  await savePageTranslation(docId, pageIndex, paragraphs, translations);

  return {
    paragraphs,
    translations,
    fromCache: false,
  };
}

/**
 * Translate a range of pages around the current page.
 * Translates current page +/- 2 pages (5 pages total).
 *
 * @param docId - Document ID
 * @param currentPage - Current page (0-indexed)
 * @param numPages - Total number of pages in document
 * @param iframe - PDF viewer iframe reference
 * @param onProgress - Callback for progress updates
 * @param forceRefreshCurrentPage - If true, force re-translate the current page (clears its cache)
 * @returns All paragraphs and translations for the range
 */
export async function translatePageRange(
  docId: string,
  currentPage: number,
  numPages: number,
  iframe: HTMLIFrameElement,
  onProgress: (message: string) => void,
  forceRefreshCurrentPage: boolean = false
): Promise<{
  paragraphs: PdfParagraph[];
  translations: TranslatedParagraph[];
}> {
  // Calculate page range: current +/- 2, clamped to valid range
  const startPage = Math.max(0, currentPage - 2);
  const endPage = Math.min(numPages - 1, currentPage + 2);

  onProgress(`Translating pages ${startPage + 1} to ${endPage + 1}...`);

  const allParagraphs: PdfParagraph[] = [];
  const allTranslations: TranslatedParagraph[] = [];

  let colorIndex = 0;

  for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
    const pageNum = pageIndex - startPage + 1;
    const totalPages = endPage - startPage + 1;
    onProgress(`Translating page ${pageNum}/${totalPages}...`);

    // Force refresh only for the current page if requested
    const forceRefresh = forceRefreshCurrentPage && pageIndex === currentPage;

    const result = await translatePage(
      docId,
      iframe,
      pageIndex,
      colorIndex,
      forceRefresh
    );

    allParagraphs.push(...result.paragraphs);
    allTranslations.push(...result.translations);

    // Increment color index for next page
    colorIndex += result.paragraphs.length;

    if (result.fromCache) {
      onProgress(`Page ${pageNum}/${totalPages} loaded from cache`);
    }
  }

  onProgress(`Translation complete: ${allTranslations.length} paragraphs`);

  return {
    paragraphs: allParagraphs,
    translations: allTranslations,
  };
}

/**
 * Check translation service health.
 */
export async function checkTranslationHealth(): Promise<{
  status: string;
  hasApiKey: boolean;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/translate/health`);
    return response.json();
  } catch {
    return {
      status: "error",
      hasApiKey: false,
    };
  }
}
